import { cn } from '@/lib/utils';

interface ProgressBarProps {
  progress: number; // 0-100
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'success' | 'warning' | 'error';
  showPercentage?: boolean;
  animated?: boolean;
}

export function ProgressBar({
  progress,
  className,
  size = 'md',
  variant = 'default',
  showPercentage = false,
  animated = true
}: ProgressBarProps) {
  const sizeClasses = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-3'
  };

  const variantClasses = {
    default: 'bg-blueviolet',
    success: 'bg-success-dark',
    warning: 'bg-warning',
    error: 'bg-error'
  };

  const clampedProgress = Math.min(Math.max(progress, 0), 100);

  return (
    <div className={cn('w-full', className)}>
      <div className={cn(
        'w-full bg-night-100 rounded-full overflow-hidden',
        sizeClasses[size]
      )}>
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500 ease-out',
            variantClasses[variant],
            animated && 'transition-all duration-500 ease-out'
          )}
          style={{ width: `${clampedProgress}%` }}
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      {showPercentage && (
        <div className="mt-1 text-right">
          <span className="font-body text-xs font-medium text-night-500">
            {Math.round(clampedProgress)}%
          </span>
        </div>
      )}
    </div>
  );
}

interface StepProgressBarProps {
  currentStep: number;
  totalSteps: number;
  className?: string;
  showStepNumbers?: boolean;
}

export function StepProgressBar({
  currentStep,
  totalSteps,
  className,
  showStepNumbers = true
}: StepProgressBarProps) {
  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className={cn('w-full', className)}>
      {showStepNumbers && (
        <div className="flex justify-between mb-2">
          <span className="font-body text-sm font-medium text-night-500">
            Step {currentStep} of {totalSteps}
          </span>
          <span className="font-body text-sm font-medium text-night-500">
            {Math.round(progress)}%
          </span>
        </div>
      )}
      <ProgressBar progress={progress} />
    </div>
  );
}

interface FormProgressBarProps {
  completedFields: number;
  totalFields: number;
  className?: string;
  label?: string;
}

export function FormProgressBar({
  completedFields,
  totalFields,
  className,
  label = 'Form completion'
}: FormProgressBarProps) {
  const progress = (completedFields / totalFields) * 100;

  return (
    <div className={cn('w-full', className)}>
      <div className="flex justify-between items-center mb-2">
        <span className="font-body text-sm font-medium text-night-500">
          {label}
        </span>
        <span className="font-body text-sm text-night-500">
          {completedFields}/{totalFields} fields
        </span>
      </div>
      <ProgressBar 
        progress={progress} 
        size="sm"
        variant={progress === 100 ? 'success' : 'default'}
      />
    </div>
  );
}

interface CircularProgressProps {
  progress: number; // 0-100
  size?: number;
  strokeWidth?: number;
  className?: string;
  showPercentage?: boolean;
  variant?: 'default' | 'success' | 'warning' | 'error';
}

export function CircularProgress({
  progress,
  size = 64,
  strokeWidth = 4,
  className,
  showPercentage = true,
  variant = 'default'
}: CircularProgressProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const clampedProgress = Math.min(Math.max(progress, 0), 100);
  const strokeDashoffset = circumference - (clampedProgress / 100) * circumference;

  const variantColors = {
    default: 'stroke-blush-500',
    success: 'stroke-green-500',
    warning: 'stroke-amber-500',
    error: 'stroke-red-500'
  };

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          className="text-night-100"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={cn('transition-all duration-500 ease-out', variantColors[variant])}
        />
      </svg>
      {showPercentage && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-body text-sm font-semibold text-night-700">
            {Math.round(clampedProgress)}%
          </span>
        </div>
      )}
    </div>
  );
}